import { gql } from "graphql-request";
import { useGraphqlMutation, useGraphqlQuery } from "@/lib/graphql/useGraphql";

// ─── Types ────────────────────────────────────────────────────────────────────

export type AdminProjectSummary = {
  id: string;
  name: string;
};

export type AdminUserRow = {
  id: string;
  name: string;
  email: string;
  created_at: string;
  org_id: string;
  org_name: string;
  projects: AdminProjectSummary[];
  status: string;
  last_login_at: string | null;
  deleted_at: string | null;
};

export type AdminProjectRow = {
  id: string;
  name: string;
  org_id: string;
  org_name: string;
  owner_emails: string[];
  owner_user_ids: string[];
  created_at: string;
  allowlisted: boolean;
  /** Project-scoped monthly spend limit in micro-USD; null when unset. */
  monthly_spend_limit_micro: string | null;
};

export type AdminOrgRow = {
  id: string;
  name: string;
  slug: string | null;
  created_at: string;
  owner_emails: string[];
  owner_user_ids: string[];
  project_count: number;
  projects: AdminProjectRow[];
};

export type AdminCreditAdjustmentRow = {
  id: string;
  org_id: string;
  org_name: string;
  amount_micro: string;
  reason: string | null;
  granted_by_email: string | null;
  created_at: string;
};

// ─── Documents ────────────────────────────────────────────────────────────────

const ALL_USERS_QUERY = gql`
  query AdminAllUsers($include_deleted: Boolean) {
    adminAllUsers(include_deleted: $include_deleted) {
      id
      name
      email
      created_at
      org_id
      org_name
      status
      last_login_at
      deleted_at
      projects {
        id
        name
      }
    }
  }
`;

const ALL_PROJECTS_QUERY = gql`
  query AdminAllProjects {
    adminAllProjects {
      id
      name
      org_id
      org_name
      owner_emails
      owner_user_ids
      created_at
      allowlisted
      monthly_spend_limit_micro
    }
  }
`;

const ALL_ORGS_QUERY = gql`
  query AdminAllOrgs {
    adminAllOrgs {
      id
      name
      slug
      created_at
      owner_emails
      owner_user_ids
      project_count
      projects {
        id
        name
        org_id
        org_name
        owner_emails
        owner_user_ids
        created_at
        allowlisted
        monthly_spend_limit_micro
      }
    }
  }
`;

const RECENT_ADJUSTMENTS_QUERY = gql`
  query AdminRecentCreditAdjustments($limit: Int) {
    adminRecentCreditAdjustments(limit: $limit) {
      id
      org_id
      org_name
      amount_micro
      reason
      granted_by_email
      created_at
    }
  }
`;

const ALLOWLIST_ADD_MUTATION = gql`
  mutation AdminCapacityAdd($project_id: ID!) {
    adminCapacityAllowlistAdd(project_id: $project_id) {
      id
      entity_id
    }
  }
`;

const ALLOWLIST_REMOVE_MUTATION = gql`
  mutation AdminCapacityRemove($project_id: ID!) {
    adminCapacityAllowlistRemove(project_id: $project_id)
  }
`;

// ─── Hooks ────────────────────────────────────────────────────────────────────

export const useAdminAllUsers = (includeDeleted = false, enabled = true) =>
  useGraphqlQuery<{ adminAllUsers: AdminUserRow[] }, { include_deleted: boolean }>({
    queryKey: ["adminAllUsers", includeDeleted],
    document: ALL_USERS_QUERY,
    variables: { include_deleted: includeDeleted },
    enabled,
  });

export const useAdminAllProjects = (enabled = true) =>
  useGraphqlQuery<{ adminAllProjects: AdminProjectRow[] }>({
    queryKey: ["adminAllProjects"],
    document: ALL_PROJECTS_QUERY,
    enabled,
  });

export const useAdminAllOrgs = (enabled = true) =>
  useGraphqlQuery<{ adminAllOrgs: AdminOrgRow[] }>({
    queryKey: ["adminAllOrgs"],
    document: ALL_ORGS_QUERY,
    enabled,
  });

export const useAdminRecentCreditAdjustments = (limit = 100, enabled = true) =>
  useGraphqlQuery<{ adminRecentCreditAdjustments: AdminCreditAdjustmentRow[] }, { limit: number }>({
    queryKey: ["adminRecentCreditAdjustments", limit],
    document: RECENT_ADJUSTMENTS_QUERY,
    variables: { limit },
    enabled,
  });

export const useAdminAllowlistAdd = () =>
  useGraphqlMutation<{ adminCapacityAllowlistAdd: { id: string; entity_id: string } }, { project_id: string }>({
    document: ALLOWLIST_ADD_MUTATION,
  });

export const useAdminAllowlistRemove = () =>
  useGraphqlMutation<{ adminCapacityAllowlistRemove: boolean }, { project_id: string }>({
    document: ALLOWLIST_REMOVE_MUTATION,
  });
